/**
 * TUVI - Modal Editar Proveedor
 * Con configuración de múltiples retenciones tributarias
 */

import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './ModalEditarProveedor.css';

const ModalEditarProveedor = ({ show, onClose, proveedor, onProveedorActualizado }) => {
  const { success, error } = useNotification();
  const [formData, setFormData] = useState({
    nombre: '',
    nit: '',
    tipo: 'materiales',
    clasificacion: 'proveedor',
    contacto_nombre: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    banco: '',
    tipo_cuenta: '',
    numero_cuenta: '',
    titular_cuenta: '',
    notas: '',
    regimen_tributario: 'responsable_iva',
    tipo_contribuyente: 'sujeto_retencion'
  });

  // Estado para múltiples retenciones
  const [retenciones, setRetenciones] = useState([]);
  const [tiposRetencion, setTiposRetencion] = useState([]);
  const [tiposProveedor, setTiposProveedor] = useState([]);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [cargandoTipos, setCargandoTipos] = useState(true);

  // Cargar tipos de retención y tipos de proveedor al montar
  useEffect(() => {
    cargarTiposRetencion();
    cargarTiposProveedor();
  }, []);

  const cargarTiposProveedor = async () => {
    try {
      const rData = await client.query(`{ tiposProveedor { id codigo nombre color } }`);
      const data = { success: true, tipos: rData.tiposProveedor };
      if (data.success) {
        setTiposProveedor(data.tipos);
      }
    } catch (error) {
      console.error('Error cargando tipos de proveedor:', error);
    }
  };

  // Cargar datos del proveedor cuando cambia
  useEffect(() => {
    if (proveedor && show) {
      setFormData({
        nombre: proveedor.nombre || '',
        nit: proveedor.nit || '',
        tipo: proveedor.tipo || 'materiales',
        clasificacion: proveedor.clasificacion || 'proveedor',
        contacto_nombre: proveedor.contacto_nombre || '',
        telefono: proveedor.telefono || '',
        email: proveedor.email || '',
        direccion: proveedor.direccion || '',
        ciudad: proveedor.ciudad || '',
        banco: proveedor.banco || '',
        tipo_cuenta: proveedor.tipo_cuenta || '',
        numero_cuenta: proveedor.numero_cuenta || '',
        titular_cuenta: proveedor.titular_cuenta || '',
        notas: proveedor.notas || '',
        regimen_tributario: proveedor.regimen_tributario || 'responsable_iva',
        tipo_contribuyente: proveedor.tipo_contribuyente || 'sujeto_retencion'
      });

      // Cargar retenciones existentes
      if (proveedor.retenciones && proveedor.retenciones.length > 0) {
        setRetenciones(proveedor.retenciones.map(r => ({
          tipo_retencion_id: r.tipo_retencion_id?.toString() || '',
          porcentaje_retencion: r.porcentaje_retencion?.toString() || '',
          base_retencion: r.base_retencion?.toString() || '',
          base_retencion_texto: r.base_retencion_texto || '',
          es_principal: r.es_principal == 1
        })));
      } else {
        // Si no hay retenciones, mostrar una vacía
        setRetenciones([{
          tipo_retencion_id: '',
          porcentaje_retencion: '',
          base_retencion: '',
          base_retencion_texto: '',
          es_principal: true
        }]);
      }
    }
  }, [proveedor, show]);

  const cargarTiposRetencion = async () => {
    setCargandoTipos(true);
    try {
      const rData = await client.query(`{ tiposRetencion { id codigo nombre descripcion porcentaje_comun base_comun } }`);
      setTiposRetencion(rData.tiposRetencion || []);
    } catch (error) {
      console.error('Error cargando tipos de retención:', error);
    } finally {
      setCargandoTipos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errores[name]) {
      setErrores(prev => ({ ...prev, [name]: null }));
    }
  };

  // Manejar cambios en una retención específica
  const handleRetencionChange = (index, field, value) => {
    const nuevasRetenciones = [...retenciones];
    nuevasRetenciones[index] = {
      ...nuevasRetenciones[index],
      [field]: value
    };

    // Si cambia el tipo de retención, usar valores actuales del backend
    if (field === 'tipo_retencion_id' && value) {
      const tipoSeleccionado = tiposRetencion.find(t => t.id.toString() === value);
      if (tipoSeleccionado) {
        // Priorizar datos del backend (sincronizados con actualización masiva)
        const pct = tipoSeleccionado.porcentaje_comun || getDefaultsForType(tipoSeleccionado.codigo).porcentaje;
        const base = tipoSeleccionado.base_comun || getDefaultsForType(tipoSeleccionado.codigo).base;
        const baseNum = parseFloat(base) || 0;
        nuevasRetenciones[index] = {
          ...nuevasRetenciones[index],
          porcentaje_retencion: pct.toString(),
          base_retencion: base.toString(),
          base_retencion_texto: baseNum > 0 ? new Intl.NumberFormat('es-CO').format(baseNum) : 'DESDE CERO'
        };
      }
    }

    setRetenciones(nuevasRetenciones);
  };

  // Fallbacks solo si el backend no tiene datos
  const getDefaultsForType = (codigo) => {
    const defaults = {
      'IVA_REGIMEN_SIMPLE': { porcentaje: '15', base: '100000' },
      'HONORARIOS': { porcentaje: '11', base: '0' },
      'COMISIONES': { porcentaje: '11', base: '0' },
      'SERVICIOS': { porcentaje: '4', base: '100000' },
      'ARRENDAMIENTOS': { porcentaje: '4', base: '0' },
      'COMPRA': { porcentaje: '2.5', base: '498000' },
      'CONSTRUCCION': { porcentaje: '2', base: '498000' }
    };
    return defaults[codigo] || { porcentaje: '', base: '' };
  };

  // Agregar nueva retención
  const agregarRetencion = () => {
    if (retenciones.length < 5) {
      setRetenciones([...retenciones, {
        tipo_retencion_id: '',
        porcentaje_retencion: '',
        base_retencion: '',
        base_retencion_texto: '',
        es_principal: false
      }]);
    }
  };

  // Eliminar retención
  const eliminarRetencion = (index) => {
    if (retenciones.length > 1) {
      const nuevasRetenciones = retenciones.filter((_, i) => i !== index);
      // Asegurar que al menos una sea principal
      if (!nuevasRetenciones.some(r => r.es_principal)) {
        nuevasRetenciones[0].es_principal = true;
      }
      setRetenciones(nuevasRetenciones);
    }
  };

  // Establecer como principal
  const establecerPrincipal = (index) => {
    const nuevasRetenciones = retenciones.map((r, i) => ({
      ...r,
      es_principal: i === index
    }));
    setRetenciones(nuevasRetenciones);
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!formData.nombre.trim()) {
      nuevosErrores.nombre = 'El nombre del proveedor es requerido';
    }

    if (!formData.tipo) {
      nuevosErrores.tipo = 'El tipo de proveedor es requerido';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      nuevosErrores.email = 'Email inválido';
    }

    // Validar porcentajes de retenciones
    retenciones.forEach((ret, index) => {
      if (ret.tipo_retencion_id && ret.porcentaje_retencion) {
        const porcentaje = parseFloat(ret.porcentaje_retencion);
        if (isNaN(porcentaje) || porcentaje < 0 || porcentaje > 100) {
          nuevosErrores[`retencion_${index}`] = 'Porcentaje debe estar entre 0 y 100';
        }
      }
    });

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setGuardando(true);

    try {
      // Filtrar retenciones vacías
      const retencionesValidas = retenciones.filter(r => r.tipo_retencion_id);

      const dataToSend = {
        id: proveedor.id,
        ...formData,
        retenciones: retencionesValidas.map((r, index) => ({
          tipo_retencion_id: parseInt(r.tipo_retencion_id),
          porcentaje_retencion: parseFloat(r.porcentaje_retencion) || 0,
          base_retencion: parseFloat(r.base_retencion) || 0,
          base_retencion_texto: r.base_retencion_texto || '',
          es_principal: index === 0 || r.es_principal
        }))
      };

      const gqlData = await client.mutate(`
        mutation($id: Int!, $input: ActualizarProveedorInput!) {
          actualizarProveedor(id: $id, input: $input) { success message }
        }
      `, { id: dataToSend.id, input: {
        nombre: dataToSend.nombre, tipo: dataToSend.tipo, clasificacion: dataToSend.clasificacion,
        nit: dataToSend.nit, contacto_nombre: dataToSend.contacto_nombre, telefono: dataToSend.telefono,
        email: dataToSend.email, direccion: dataToSend.direccion, ciudad: dataToSend.ciudad,
        banco: dataToSend.banco, numero_cuenta: dataToSend.numero_cuenta, tipo_cuenta: dataToSend.tipo_cuenta, titular_cuenta: dataToSend.titular_cuenta,
        notas: dataToSend.notas, regimen_tributario: dataToSend.regimen_tributario,
        tipo_contribuyente: dataToSend.tipo_contribuyente,
        retenciones: dataToSend.retenciones
      }});

      if (gqlData.actualizarProveedor.success) {
        success('Proveedor actualizado exitosamente');
        onProveedorActualizado(dataToSend);
        handleClose();
      } else {
        error('Error: ' + gqlData.actualizarProveedor.message);
      }
    } catch (err) {
      console.error('Error al actualizar proveedor:', err);
      error('Error al actualizar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  const handleClose = () => {
    setErrores({});
    onClose();
  };

  const formatearMoneda = (valor) => {
    if (!valor) return '$0';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(valor);
  };

  // Obtener tipos de retención ya usados (para evitar duplicados)
  const tiposUsados = retenciones.map(r => r.tipo_retencion_id).filter(Boolean);

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content modal-editar-proveedor" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Proveedor</h2>
          <button className="modal-close" onClick={handleClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="proveedor-form">
          {/* Información General */}
          <div className="form-section">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Información General
            </h3>

            <div className="form-row form-row-4">
              <div className="form-group">
                <label>Nombre del Proveedor *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className={errores.nombre ? 'error' : ''}
                  placeholder="Nombre completo del proveedor"
                />
                {errores.nombre && (
                  <span className="error-message">{errores.nombre}</span>
                )}
              </div>

              <div className="form-group">
                <label>NIT / Cédula</label>
                <input
                  type="text"
                  name="nit"
                  value={formData.nit}
                  onChange={handleChange}
                  placeholder="Identificación fiscal"
                />
              </div>

              <div className="form-group">
                <label>Tipo de Proveedor *</label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  className={errores.tipo ? 'error' : ''}
                >
                  {tiposProveedor.length > 0 ? (
                    tiposProveedor.map(tipo => (
                      <option key={tipo.codigo} value={tipo.codigo}>
                        {tipo.nombre}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="materiales">Materiales</option>
                      <option value="mano_obra">Mano de Obra</option>
                      <option value="servicios">Servicios</option>
                      <option value="transporte">Transporte</option>
                      <option value="equipos">Equipos</option>
                      <option value="otro">Otro</option>
                    </>
                  )}
                </select>
                {errores.tipo && (
                  <span className="error-message">{errores.tipo}</span>
                )}
              </div>

              <div className="form-group">
                <label>Clasificación *</label>
                <select
                  name="clasificacion"
                  value={formData.clasificacion}
                  onChange={handleChange}
                >
                  <option value="proveedor">Proveedor</option>
                  <option value="contratista">Contratista</option>
                  <option value="administrativo">Administrativo</option>
                </select>
              </div>
            </div>
          </div>

          {/* Configuración Tributaria - Múltiples Retenciones */}
          <div className="form-section form-section-tributaria">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Retenciones Tributarias ({retenciones.filter(r => r.tipo_retencion_id).length})
            </h3>

            <div className="tributaria-info-banner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
              <span>Un proveedor puede tener múltiples tipos de retención según el tipo de operación.</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Régimen Tributario</label>
                <select
                  name="regimen_tributario"
                  value={formData.regimen_tributario}
                  onChange={handleChange}
                >
                  <option value="responsable_iva">Responsable de IVA</option>
                  <option value="no_responsable_iva">No responsable de IVA</option>
                  <option value="gran_contribuyente">Gran Contribuyente</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tipo de Contribuyente</label>
                <select
                  name="tipo_contribuyente"
                  value={formData.tipo_contribuyente}
                  onChange={handleChange}
                >
                  <option value="autorretenedor">Autorretenedor</option>
                  <option value="sujeto_retencion">Sujeto a Retención</option>
                  <option value="regimen_simple">Régimen Simple</option>
                </select>
              </div>
            </div>

            {/* Lista de Retenciones */}
            <div className="retenciones-list">
              {retenciones.map((retencion, index) => (
                <div key={index} className={`retencion-form-item ${retencion.es_principal ? 'principal' : ''}`}>
                  <div className="retencion-form-header">
                    <span className="retencion-numero">Retención #{index + 1}</span>
                    <div className="retencion-form-actions">
                      {!retencion.es_principal && retencion.tipo_retencion_id && (
                        <button
                          type="button"
                          className="btn-hacer-principal"
                          onClick={() => establecerPrincipal(index)}
                          title="Establecer como principal"
                        >
                          Principal
                        </button>
                      )}
                      {retencion.es_principal && (
                        <span className="badge-es-principal">Principal</span>
                      )}
                      {retenciones.length > 1 && (
                        <button
                          type="button"
                          className="btn-eliminar-retencion"
                          onClick={() => eliminarRetencion(index)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="retencion-form-row">
                    <div className="form-group">
                      <label>Tipo de Retención</label>
                      <select
                        value={retencion.tipo_retencion_id}
                        onChange={(e) => handleRetencionChange(index, 'tipo_retencion_id', e.target.value)}
                        disabled={cargandoTipos}
                      >
                        <option value="">Seleccionar tipo...</option>
                        {tiposRetencion.map(tipo => (
                          <option
                            key={tipo.id}
                            value={tipo.id}
                            disabled={tiposUsados.includes(tipo.id.toString()) && retencion.tipo_retencion_id !== tipo.id.toString()}
                          >
                            {tipo.nombre} {tiposUsados.includes(tipo.id.toString()) && retencion.tipo_retencion_id !== tipo.id.toString() ? '(ya asignado)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>% Retención</label>
                      <div className="input-with-suffix">
                        <input
                          type="number"
                          value={retencion.porcentaje_retencion}
                          onChange={(e) => handleRetencionChange(index, 'porcentaje_retencion', e.target.value)}
                          placeholder="0"
                          step="0.01"
                          min="0"
                          max="100"
                        />
                        <span className="input-suffix">%</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Base Mínima</label>
                      <div className="input-with-prefix">
                        <span className="input-prefix">$</span>
                        <input
                          type="number"
                          value={retencion.base_retencion}
                          onChange={(e) => handleRetencionChange(index, 'base_retencion', e.target.value)}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  {errores[`retencion_${index}`] && (
                    <span className="error-message">{errores[`retencion_${index}`]}</span>
                  )}
                </div>
              ))}
            </div>

            {retenciones.length < 5 && (
              <button type="button" className="btn-agregar-retencion" onClick={agregarRetencion}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
                Agregar otra retención
              </button>
            )}
          </div>

          {/* Datos de Contacto */}
          <div className="form-section">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Datos de Contacto
            </h3>

            <div className="form-row form-row-4">
              <div className="form-group">
                <label>Nombre del Contacto</label>
                <input
                  type="text"
                  name="contacto_nombre"
                  value={formData.contacto_nombre}
                  onChange={handleChange}
                  placeholder="Persona de contacto"
                />
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="text"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="Número de teléfono"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errores.email ? 'error' : ''}
                  placeholder="correo@ejemplo.com"
                />
                {errores.email && (
                  <span className="error-message">{errores.email}</span>
                )}
              </div>

              <div className="form-group">
                <label>Ciudad</label>
                <input
                  type="text"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleChange}
                  placeholder="Ciudad"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Dirección completa"
              />
            </div>
          </div>

          {/* Información Bancaria */}
          <div className="form-section">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
              Información Bancaria
            </h3>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Banco</label>
                <input
                  type="text"
                  name="banco"
                  value={formData.banco}
                  onChange={handleChange}
                  placeholder="Nombre del banco"
                />
              </div>

              <div className="form-group">
                <label>Tipo de Cuenta</label>
                <select
                  name="tipo_cuenta"
                  value={formData.tipo_cuenta}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar</option>
                  <option value="ahorros">Ahorros</option>
                  <option value="corriente">Corriente</option>
                </select>
              </div>

              <div className="form-group">
                <label>Número de Cuenta</label>
                <input
                  type="text"
                  name="numero_cuenta"
                  value={formData.numero_cuenta}
                  onChange={handleChange}
                  placeholder="Número de cuenta bancaria"
                />
              </div>
              <div className="form-group">
                <label>A Nombre de</label>
                <input
                  type="text"
                  name="titular_cuenta"
                  value={formData.titular_cuenta}
                  onChange={handleChange}
                  placeholder="Titular de la cuenta"
                />
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="form-section">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Notas
            </h3>
            <div className="form-group">
              <textarea
                name="notas"
                value={formData.notas}
                onChange={handleChange}
                rows="3"
                placeholder="Notas adicionales sobre el proveedor..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancelar" onClick={handleClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-guardar" disabled={guardando}>
              {guardando ? (
                <>
                  <span className="btn-spinner"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEditarProveedor;
